'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronDown, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNav } from './context.js';
import { NavIcon, WaffleIcon } from './icons.js';
/**
 * The wordmark, compiled in. It used to come from the payload's `logo` block, which meant
 * a static piece of chrome could disagree with the compiled-in fallback and shift on load
 * like everything else fetched. Nobody renames the ArtHelper wordmark from an admin
 * screen, so it belongs in the release.
 *
 * The href is the site root, NOT the resolved `home` catalog entry. Deriving it was
 * considered and rejected twice over: it would put this back on config, and Home points at
 * /community while the wordmark has always gone to the root — they are different
 * destinations that happen to share a domain, not one destination expressed twice.
 */
/**
 * Two halves so they can carry different weights — bold "art", regular "helper". Rendered
 * as adjacent spans with no whitespace between them, so the anchor's text content is still
 * the single word "arthelper" and its accessible name is unchanged.
 */
const WORDMARK_STRONG = 'art';
const WORDMARK_LIGHT = 'helper';
const WORDMARK_HREF = 'https://www.arthelper.com';
function chipClass(entry) {
    return entry.active ? 'ahsuite-chip ahsuite-chip--active' : 'ahsuite-chip';
}
function Chip({ entry }) {
    return (_jsx("span", { className: chipClass(entry), children: _jsx(NavIcon, { name: entry.app.icon }) }));
}
/**
 * One desktop tile. A real anchor, so the browser navigates natively and the
 * address bar becomes the destination — `activate` only fires the host's seam.
 * The host's own app without a destination renders as an inert indicator instead.
 */
function Tile({ entry, onActivate }) {
    const className = entry.active ? 'ahsuite-tile ahsuite-tile--active' : 'ahsuite-tile';
    if (!entry.navigable) {
        return (_jsxs("span", { "aria-current": entry.active ? 'page' : undefined, className: className, children: [_jsx(Chip, { entry: entry }), _jsx("span", { className: "ahsuite-label", children: entry.app.label })] }));
    }
    return (_jsxs("a", { "aria-current": entry.active ? 'page' : undefined, className: className, href: entry.app.href, onClick: () => onActivate(entry), children: [_jsx(Chip, { entry: entry }), _jsx("span", { className: "ahsuite-label", children: entry.app.label })] }));
}
/** One dropdown row below the md breakpoint. Shares Tile's activation handler exactly. */
function Row({ entry, onActivate }) {
    const className = entry.active ? 'ahsuite-row ahsuite-row--active' : 'ahsuite-row';
    if (!entry.navigable) {
        return (_jsxs("span", { "aria-current": entry.active ? 'page' : undefined, className: className, children: [_jsx(Chip, { entry: entry }), _jsx("span", { className: "ahsuite-row-label", children: entry.app.label })] }));
    }
    return (_jsxs("a", { "aria-current": entry.active ? 'page' : undefined, className: className, href: entry.app.href, onClick: () => onActivate(entry), children: [_jsx(Chip, { entry: entry }), _jsx("span", { className: "ahsuite-row-label", children: entry.app.label })] }));
}
function NavBar({ sticky = false, className }) {
    const { apps, activeApp, collapsed, toggle, activate } = useNav();
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const triggerRef = useRef(null);
    // The dropdown must be dismissable without leaving the page: Escape, the Close
    // button, or a pointer anywhere outside it.
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            setOpen(false);
            triggerRef.current?.focus();
        };
        const onPointerDown = (event) => {
            const target = event.target;
            if (!target) {
                return;
            }
            if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open]);
    // Collapsing while the dropdown is open must not leave it open to reappear on the
    // next expand. This handles that one direction only, and deliberately so: it fires
    // on a CHANGE of `collapsed`, so it could never have policed a panel opened while
    // already collapsed. What makes that unreachable is the stylesheet — a collapsed
    // `.ahsuite-bar` is `visibility: hidden`, which takes `.ahsuite-trigger` out of the
    // tab order and the accessibility tree, so there is no way left to reach the control
    // that opens the panel.
    //
    // This effect carries more weight than it used to. The panel is now a SIBLING of the
    // bar rather than a descendant (see the stacking note below), so the bar's collapsed
    // `visibility: hidden` no longer reaches it — an open panel would otherwise survive a
    // collapse on screen with its trigger gone. Between the two, `hidden` on the panel is
    // what removes it, and this is what sets it.
    useEffect(() => {
        if (!collapsed) {
            return;
        }
        setOpen(false);
    }, [collapsed]);
    const onActivate = useCallback((entry) => {
        activate(entry);
        setOpen(false);
    }, [activate]);
    const classes = ['ahsuite-bar', sticky ? 'ahsuite-bar--sticky' : '', className ?? ''].filter(Boolean).join(' ');
    return (_jsxs(_Fragment, { children: [_jsxs("div", { "aria-label": "ArtHelper suite", className: classes, "data-collapsed": collapsed ? 'true' : 'false', role: "navigation", children: [_jsxs("div", { className: "ahsuite-brand", children: [_jsx("button", { "aria-expanded": !collapsed, "aria-label": collapsed ? 'Show the suite bar' : 'Hide the suite bar', className: "ahsuite-waffle", onClick: toggle, type: "button", children: _jsx(WaffleIcon, {}) }), _jsxs("a", { "aria-label": `${WORDMARK_STRONG}${WORDMARK_LIGHT}`, className: "ahsuite-wordmark", href: WORDMARK_HREF, children: [_jsx("span", { className: "ahsuite-wordmark-strong", children: WORDMARK_STRONG }), _jsx("span", { className: "ahsuite-wordmark-light", children: WORDMARK_LIGHT })] })] }), _jsx("div", { className: "ahsuite-tiles", children: apps.map((entry) => (_jsx(Tile, { entry: entry, onActivate: onActivate }, entry.app.id))) }), _jsxs("button", { "aria-controls": "ahsuite-panel", "aria-expanded": open, "aria-haspopup": "true", className: "ahsuite-trigger", onClick: () => setOpen((current) => !current), ref: triggerRef, type: "button", children: [activeApp ? _jsx(Chip, { entry: activeApp }) : null, _jsx("span", { className: "ahsuite-trigger-label", children: activeApp?.app.label ?? 'Apps' }), _jsx(ChevronDown, { "aria-hidden": "true", className: "ahsuite-chevron", height: 18, strokeWidth: 2, width: 18 })] }), _jsx("span", { "aria-hidden": "true", className: "ahsuite-balance" })] }), _jsxs("div", { "aria-label": "Apps", className: "ahsuite-panel", hidden: !open, id: "ahsuite-panel", ref: panelRef, role: "dialog", children: [_jsxs("div", { className: "ahsuite-panel-head", children: [_jsx("span", { className: "ahsuite-panel-title", children: "Apps" }), _jsxs("button", { "aria-label": "Close", className: "ahsuite-close", onClick: () => setOpen(false), type: "button", children: [_jsx(X, { "aria-hidden": "true", className: "ahsuite-icon", height: 14, strokeWidth: 2, width: 14 }), _jsx("span", { children: "Close" })] })] }), apps.map((entry) => (_jsx(Row, { entry: entry, onActivate: onActivate }, entry.app.id)))] })] }));
}
export { NavBar };
//# sourceMappingURL=nav-bar.js.map